// Connector Catalog (admin-portal) — the platform surface for curating the GLOBAL
// registry of integration connector TYPES (migration-service connector_definitions).
// Operators can add/edit a definition, move it through its lifecycle (internal → beta →
// available), and delete unused ones. This governs which connectors tenants can see and
// configure as connections; it never touches a tenant's own credentials/config.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, Form, Input, Modal, Popconfirm, Row, Select, Space, Statistic, Switch, Table, Tag, Typography, message,
} from 'antd';
import { ReloadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import * as api from '../../services/connectorCatalogService';

const { Title, Text, Paragraph } = Typography;

const DIRECTIONS: api.ConnectorDirection[] = ['inbound', 'outbound', 'bidirectional'];
const TRANSPORTS: api.ConnectorTransport[] = ['csv', 'sftp', 'rest', 'webhook'];
const STATUSES: api.ConnectorStatus[] = ['available', 'beta', 'internal'];
const STATUS_COLOR: Record<api.ConnectorStatus, string> = {
  available: 'green',
  beta: 'gold',
  internal: 'default',
};

const errMsg = (e: any, fb: string) =>
  e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || fb;
const prettyJson = (v: unknown) => JSON.stringify(v ?? {}, null, 2);

export const ConnectorCatalogPage: React.FC = () => {
  const [defs, setDefs] = useState<api.ConnectorDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [certifyingKey, setCertifyingKey] = useState<string | null>(null);
  const [certifyReport, setCertifyReport] = useState<api.CertifyReport | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDefs(await api.listDefinitions());
    } catch (e) {
      message.error(errMsg(e, 'Could not load the connector catalog'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => ({
    total: defs.length,
    available: defs.filter((d) => d.status === 'available').length,
    beta: defs.filter((d) => d.status === 'beta').length,
    internal: defs.filter((d) => d.status === 'internal').length,
  }), [defs]);

  const openNew = () => {
    setEditingKey(null);
    form.resetFields();
    form.setFieldsValue({
      direction: 'inbound', transport: 'rest', status: 'internal',
      config_schema: '{\n  "fields": []\n}', credential_schema: '{\n  "fields": []\n}',
    });
    setOpen(true);
  };

  const openEdit = (d: api.ConnectorDefinition) => {
    setEditingKey(d.key);
    form.resetFields();
    form.setFieldsValue({
      ...d,
      config_schema: prettyJson(d.config_schema),
      credential_schema: prettyJson(d.credential_schema),
    });
    setOpen(true);
  };

  const save = async () => {
    let v: any;
    try {
      v = await form.validateFields();
    } catch {
      return; // form-level validation
    }
    let config_schema: Record<string, unknown>;
    let credential_schema: Record<string, unknown>;
    try {
      config_schema = JSON.parse(v.config_schema || '{}');
    } catch {
      message.error('Config schema is not valid JSON');
      return;
    }
    try {
      credential_schema = JSON.parse(v.credential_schema || '{}');
    } catch {
      message.error('Credential schema is not valid JSON');
      return;
    }
    setSaving(true);
    try {
      await api.saveDefinition({
        key: v.key,
        name: v.name,
        category: v.category,
        direction: v.direction,
        transport: v.transport,
        status: v.status,
        target_key: v.target_key || undefined,
        docs_url: v.docs_url || undefined,
        config_schema,
        credential_schema,
      });
      message.success(editingKey ? 'Connector updated' : 'Connector added');
      setOpen(false);
      await load();
    } catch (e) {
      message.error(errMsg(e, 'Could not save the connector'));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (d: api.ConnectorDefinition, status: api.ConnectorStatus) => {
    try {
      await api.setDefinitionStatus(d.key, status);
      setDefs((xs) => xs.map((x) => (x.key === d.key ? { ...x, status } : x)));
    } catch (e) {
      message.error(errMsg(e, 'Could not update status'));
    }
  };

  const changeVerified = async (d: api.ConnectorDefinition, verified: boolean) => {
    try {
      await api.setDefinitionVerified(d.key, verified);
      setDefs((xs) => xs.map((x) => (x.key === d.key ? { ...x, verified } : x)));
    } catch (e) {
      message.error(errMsg(e, 'Could not update the verified badge'));
    }
  };

  const runCertify = async (d: api.ConnectorDefinition) => {
    setCertifyingKey(d.key);
    try {
      const report = await api.certifyDefinition(d.key);
      setCertifyReport(report);
      if (report.passed) {
        setDefs((xs) => xs.map((x) => (x.key === d.key ? { ...x, verified: true } : x)));
        message.success(`${d.key} passed certification — verified`);
      } else {
        message.warning(`${d.key} did not pass — see the checks`);
      }
    } catch (e) {
      message.error(errMsg(e, 'Certification failed'));
    } finally {
      setCertifyingKey(null);
    }
  };

  const remove = async (d: api.ConnectorDefinition) => {
    try {
      await api.deleteDefinition(d.key);
      message.success(`Removed ${d.key}`);
      await load();
    } catch (e) {
      message.error(errMsg(e, 'Could not delete — it may still be in use'), 6);
    }
  };

  const columns = [
    { title: 'Key', dataIndex: 'key', render: (v: string) => <Text code>{v}</Text> },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Category', dataIndex: 'category', render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Direction', dataIndex: 'direction', render: (v: string) => <Tag color="blue">{v}</Tag> },
    { title: 'Transport', dataIndex: 'transport', render: (v: string) => <Tag color="geekblue">{v}</Tag> },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v: api.ConnectorStatus, r: api.ConnectorDefinition) => (
        <Select
          size="small"
          value={v}
          style={{ width: 120 }}
          onChange={(s) => changeStatus(r, s)}
          options={STATUSES.map((s) => ({ value: s, label: <Tag color={STATUS_COLOR[s]} style={{ margin: 0 }}>{s}</Tag> }))}
        />
      ),
    },
    {
      title: 'Verified',
      dataIndex: 'verified',
      render: (v: boolean, r: api.ConnectorDefinition) => (
        <Space>
          <Switch size="small" checked={!!v} onChange={(c) => changeVerified(r, c)} />
          {v && <Tag color="green" style={{ margin: 0 }}>✓</Tag>}
        </Space>
      ),
    },
    {
      title: 'Actions',
      render: (_: unknown, r: api.ConnectorDefinition) => (
        <Space>
          <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
          <Button size="small" loading={certifyingKey === r.key} onClick={() => runCertify(r)}>Certify</Button>
          <Popconfirm
            title={`Delete "${r.key}"?`}
            description="Refused if any tenant connection still uses it."
            okButtonProps={{ danger: true }}
            onConfirm={() => remove(r)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1200 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>Connector Catalog</Title>
          <Paragraph type="secondary" style={{ maxWidth: 720 }}>
            The platform-wide registry of integration connector <b>types</b>. Curate what tenants can connect and
            move each through its lifecycle: <Tag color="default">internal</Tag> (designed, hidden) →
            <Tag color="gold">beta</Tag> (offered, flagged) → <Tag color="green">available</Tag>. Editing here
            affects every tenant; it never touches a tenant's own connection config or credentials.
          </Paragraph>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>Add connector</Button>
        </Space>
      </Space>

      <Row gutter={16} style={{ margin: '8px 0 16px' }}>
        <Col xs={12} md={6}><Card size="small" loading={loading}><Statistic title="Total" value={stats.total} /></Card></Col>
        <Col xs={12} md={6}><Card size="small" loading={loading}><Statistic title="Available" value={stats.available} valueStyle={{ color: 'var(--color-success)' }} /></Card></Col>
        <Col xs={12} md={6}><Card size="small" loading={loading}><Statistic title="Beta" value={stats.beta} valueStyle={{ color: 'var(--color-warning)' }} /></Card></Col>
        <Col xs={12} md={6}><Card size="small" loading={loading}><Statistic title="Internal" value={stats.internal} /></Card></Col>
      </Row>

      <Card size="small">
        <Table rowKey="key" size="small" loading={loading} columns={columns as any} dataSource={defs} pagination={false} />
      </Card>

      <Modal
        title={editingKey ? `Edit "${editingKey}"` : 'Add connector'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={save}
        confirmLoading={saving}
        okText="Save"
        width={680}
      >
        <Form form={form} layout="vertical" size="small">
          <Space wrap size="middle">
            <Form.Item name="key" label="Key" rules={[{ required: true }, { pattern: /^[a-z][a-z0-9_]{1,49}$/, message: 'lowercase letters/digits/underscore, 2-50 chars' }]}>
              <Input placeholder="workday_pull" style={{ width: 200 }} disabled={!!editingKey} />
            </Form.Item>
            <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input placeholder="Workday — employee pull" style={{ width: 260 }} /></Form.Item>
            <Form.Item name="category" label="Category" rules={[{ required: true }]}><Input placeholder="file / api / hris" style={{ width: 140 }} /></Form.Item>
          </Space>
          <Space wrap size="middle">
            <Form.Item name="direction" label="Direction" rules={[{ required: true }]}><Select style={{ width: 160 }} options={DIRECTIONS.map((d) => ({ value: d, label: d }))} /></Form.Item>
            <Form.Item name="transport" label="Transport" rules={[{ required: true }]}><Select style={{ width: 140 }} options={TRANSPORTS.map((t) => ({ value: t, label: t }))} /></Form.Item>
            <Form.Item name="status" label="Status" rules={[{ required: true }]}><Select style={{ width: 140 }} options={STATUSES.map((s) => ({ value: s, label: s }))} /></Form.Item>
          </Space>
          <Space wrap size="middle">
            <Form.Item name="target_key" label="Target key (import destination)"><Input placeholder="employees" style={{ width: 200 }} /></Form.Item>
            <Form.Item name="docs_url" label="Docs URL"><Input placeholder="https://…" style={{ width: 300 }} /></Form.Item>
          </Space>
          <Alert type="info" showIcon style={{ margin: '4px 0 12px' }}
            message={'Schemas drive the tenant’s connection form — a JSON object like {"fields":[{"key":"host","type":"string","required":true}]}.'} />
          <Form.Item name="config_schema" label="Config schema (JSON)"><Input.TextArea rows={5} style={{ fontFamily: 'monospace' }} /></Form.Item>
          <Form.Item name="credential_schema" label="Credential schema (JSON)"><Input.TextArea rows={4} style={{ fontFamily: 'monospace' }} /></Form.Item>
        </Form>
      </Modal>

      <Modal
        title={certifyReport ? `Certification — ${certifyReport.key}` : 'Certification'}
        open={!!certifyReport}
        onCancel={() => setCertifyReport(null)}
        onOk={() => setCertifyReport(null)}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {certifyReport && (
          <>
            <Alert
              type={certifyReport.passed ? 'success' : 'warning'}
              showIcon
              style={{ marginBottom: 12 }}
              message={certifyReport.passed ? 'Passed — verified badge awarded.' : 'Not passed — fix the failing checks below.'}
            />
            <Space direction="vertical" style={{ width: '100%' }}>
              {certifyReport.checks.map((ck) => (
                <Space key={ck.name} style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Text>{ck.ok ? '✅' : '❌'} {ck.name}</Text>
                  {!ck.ok && ck.detail && <Text type="secondary" style={{ fontSize: 12 }}>{ck.detail}</Text>}
                </Space>
              ))}
            </Space>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ConnectorCatalogPage;
